/**
 * Raising a pre-consultation form, from the practitioner's side.
 *
 * The other half of this feature. `preconsultationService` is what the person
 * holding a link calls and carries no bearer token at all; this is the call
 * that makes the link in the first place, and it is the practitioner's own —
 * so it is a separate module rather than a third function in there, where the
 * rule is that nothing may reach for a session.
 *
 * It is here rather than in `clientsService` because it is not a client's
 * call: the same endpoint raises the same form for an enquiry, and the only
 * difference is which id goes up. A copy of it under each feature is two
 * places for the response shape to drift.
 *
 * snake_case stops here, as in every other service.
 */

import { api, API_ENDPOINTS, ApiError } from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";

/** Who a form was raised for: a client who has bought, or an enquiry who has not. */
export type PreconsultationSubjectType = "client" | "inquiry";

/**
 * Where a raised form stands, as the backend spells it.
 *
 * `PENDING` is every form this call creates — nobody has answered it yet. The
 * rest are what it becomes, and are read rather than written here.
 */
export type PreconsultationFormStatus =
  | "PENDING"
  | "SUBMITTED"
  | "APPROVED"
  | "REVIEWED"
  | "REJECTED"
  | "EXPIRED";

/**
 * The response, as far as this app reads it.
 *
 * The endpoint answers with the whole form record — some forty fields — and
 * every answer on it comes back blank or `null`, because it has just been
 * raised and nobody has filled it in. Typing all forty here would be typing
 * out a shape whose only reader is a screen that does not exist yet: what is
 * declared is what is actually read, and the rest arrives and is ignored.
 *
 * When something does come to read a submitted form, the answers belong in
 * `preconsultation.types` beside `PreconsultationPayload` — which is the same
 * shape going the other way — rather than being grown onto this.
 */
interface PreconsultationFormResponse {
  public_id?: string | null;
  subject?: {
    type?: string | null;
    public_id?: string | null;
    name?: string | null;
  } | null;
  status?: string | null;
  status_display?: string | null;
  /** ISO datetime; the moment the link stops working. */
  expires_at?: string | null;
  /** The token on its own. Not used — `share_url` already carries it. */
  share_token?: string | null;
  /** The whole address, built by the server. The only thing worth copying. */
  share_url?: string | null;
}

/** A raised form, in the app's own casing — what the dialog that hands it over reads. */
export interface PreconsultationFormLink {
  /** The form's own id, for anything that later reads what came back. */
  id: string;
  /** The whole address, exactly as the server built it. Never assembled here. */
  url: string;
  /** ISO datetime, or `null` where the backend named no expiry. */
  expiresAt: string | null;
  /** `PENDING` on everything this call raises. */
  status: PreconsultationFormStatus;
  /** Who it was raised for, as the backend named them back. */
  subject: {
    type: PreconsultationSubjectType | null;
    id: string;
    name: string;
  };
}

/* ── A form as it is read back off a record ──────────────────────────────── */

/**
 * One entry of the `pre_consultation_forms` list a client — or, one day, an
 * enquiry — comes back carrying. As far as this app reads it.
 *
 * The backend puts the whole form here, answers and all: forty-odd fields, the
 * same ones `PreconsultationPayload` sends the other way. Declared here is what
 * is actually read, which is where the form *stands* rather than what is in it
 * — that is the strip above the client's tabs, and the strip says whether the
 * questions have been answered, not what the answers were. When a screen comes
 * to draw the answers themselves, they belong beside `PreconsultationPayload`
 * in `preconsultation.types`; this stays the state of the thing.
 */
export interface PreconsultationFormRecord {
  public_id?: string | null;
  status?: string | null;
  status_display?: string | null;
  /** ISO datetime; the moment the link stops working. */
  expires_at?: string | null;
  /** ISO datetime, or `null` while nobody has answered it. */
  submitted_at?: string | null;
  /** ISO datetime; set once the practitioner has accepted or returned it. */
  reviewed_at?: string | null;
  /** What the practitioner said when they returned it. `""` until then. */
  rejection_reason?: string | null;
  created_at?: string | null;
  /**
   * The whole address, built by the server — the same `share_url` the create
   * call answers with, carried back on the record so a link that was minted
   * in some other session can still be copied. Absent on a backend that does
   * not send it, which is why every reader of it has a fallback.
   */
  share_url?: string | null;

  /* The answers. Every one is blank or `null` until the form comes back. */
  first_name?: string | null;
  last_name?: string | null;
  /** An enum constant: `MALE`, not `Male`. */
  gender?: string | null;
  /**
   * Whole years, and `null` where the client gave a date instead.
   *
   * Both this and `date_of_birth` come back, and either can be the only one
   * answered: the form asks for a date and takes an age beside it, and a
   * practitioner filling a client in by hand far more often knows the age. See
   * `toAnswers`, which reads the age off the date where only the date is
   * there.
   */
  age?: number | null;
  /** `YYYY-MM-DD`. `null` where the question was answered as an age, or not. */
  date_of_birth?: string | null;
  email?: string | null;
  /** E.164, dialling code included. */
  phone_number?: string | null;
  full_address?: string | null;
  /** The state, under the name the form uses for it. */
  state?: string | null;
  city?: string | null;
  postal_code?: string | null;
  profession?: string | null;
  life_style?: CatalogReference | null;
  /**
   * What the client was signed up against: the practice's own services, which
   * carry a `public_id` where a catalog row carries an `id`. `goals` is the
   * same field under the name it had before that rename, and is read behind it
   * for a record the backend has not migrated.
   */
  services?: ServiceReference[] | null;
  goals?: CatalogReference[] | null;
  /** The figure and its unit, shouted: `180` and `CM`. */
  height_value?: number | null;
  height_unit?: string | null;
  weight_value?: number | null;
  weight_unit?: string | null;
  target_weight_value?: number | null;
  target_weight_unit?: string | null;
  dietary_pattern?: CatalogReference | null;
  favorite_cuisines?: CatalogReference[] | null;
  /** The same field before it grew a list, on a record not yet migrated. */
  favorite_cuisine?: CatalogReference | null;
  favorite_dishes?: string[] | null;
  foods_to_avoid?: string[] | null;
  food_allergies?: CatalogReference[] | null;
  medical_conditions?: CatalogReference[] | null;
  /** `null` where the question was never reached, which is not the same as No. */
  takes_medications?: boolean | null;
  medications?: string[] | null;
  /** `HH:MM:SS`, and `null` where no hours were given. */
  working_hours_start?: string | null;
  working_hours_end?: string | null;
  has_no_fixed_working_hours?: boolean | null;
  sleep_start?: string | null;
  sleep_end?: string | null;
  has_no_fixed_sleep_schedule?: boolean | null;
  /** Constants of this app's own making — `NEVER`, `OCCASIONAL`. */
  travel_frequency?: string | null;
  alcohol_frequency?: string | null;
  smoking_frequency?: string | null;
}

/** A `{ id, name }` row of one of the catalogs the form was served with. */
interface CatalogReference {
  id?: number | null;
  name?: string | null;
}

/** One of the practice's services, which is what a goal is now. */
interface ServiceReference {
  public_id?: string | null;
  name?: string | null;
}

/**
 * What one client actually answered, in the app's own casing.
 *
 * Values, not sentences. Units arrive as the wire shouts them and times as
 * `HH:MM:SS`, and neither is turned into something readable here — that is the
 * dialog's job, and a service that wrote "180 cm" would be deciding how a
 * screen it cannot see puts things. The same split the rest of this app keeps
 * between `clientsService` and `clientMapping`.
 *
 * Every field is blank, empty or `null` on a form nobody has answered yet.
 */
export interface PreconsultationFormAnswers {
  firstName: string;
  lastName: string;
  /** The enum constant, as it arrived — `formatEnumLabel` writes it for display. */
  gender: string;
  /** Whole years as the form sent them, and `null` where it sent a date. */
  age: number | null;
  /**
   * `YYYY-MM-DD`, and `null` where the question went unanswered.
   *
   * Kept beside the age rather than folded into it. Counting the years is a
   * screen's business and the count changes with the day it is read on, so a
   * service that did it here would be writing a value that quietly goes stale
   * in whatever holds it — `ageOn` is what does the counting, at the point of
   * drawing.
   */
  dateOfBirth: string | null;
  email: string;
  phone: string;
  address: string;
  state: string;
  city: string;
  postalCode: string;
  profession: string;
  /** Catalog names, resolved by the backend before it sent them. */
  lifestyle: string;
  goals: string[];
  foodPreference: string;
  cuisines: string[];
  conditions: string[];
  /** The figure as a string, and its unit lowercased: `"180"` and `"cm"`. */
  height: string;
  heightUnit: string;
  weight: string;
  weightUnit: string;
  targetWeight: string;
  targetWeightUnit: string;
  dishesLoved: string[];
  dishesAvoided: string[];
  foodAllergies: string[];
  takesMedications: boolean | null;
  medications: string[];
  /** `HH:MM`, trimmed of the seconds the wire carries. Empty where unanswered. */
  workStart: string;
  workEnd: string;
  /** The difference between "no fixed hours" and "nobody got that far". */
  noFixedWorkHours: boolean;
  sleepStart: string;
  sleepEnd: string;
  noFixedSleep: boolean;
  travel: string;
  alcohol: string;
  smoking: string;
}

/** Where one raised form stands, in the app's own casing. */
export interface PreconsultationFormSummary {
  /** The form's `public_id` — what a review call is made against. */
  id: string;
  status: PreconsultationFormStatus;
  /** The backend's own wording — "Submitted" — for anything that shows it raw. */
  statusDisplay: string;
  /** ISO datetimes, `null` where the moment has not come. */
  submittedAt: string | null;
  reviewedAt: string | null;
  expiresAt: string | null;
  /** Empty unless the form was returned to the client with a reason. */
  rejectionReason: string;
  /** ISO datetime; when the link was raised. What the list is ordered on. */
  createdAt: string | null;
  /**
   * The address this form is reached at, exactly as the server built it, and
   * `""` where the record came back without one. Never assembled here — a
   * caller with nothing to copy has to mint a fresh link instead.
   */
  shareUrl: string;
  /**
   * What was answered. Present whatever state the form is in, and empty
   * throughout on one nobody has filled in — a caller that draws these should
   * be looking at a `SUBMITTED` form, and nothing here enforces that because
   * an approved form's answers are worth reading back too.
   */
  answers: PreconsultationFormAnswers;
}

/** A catalog row's name, and "" for a field the backend left null. */
const nameOf = (
  row: CatalogReference | ServiceReference | null | undefined
): string => (row?.name ?? "").trim();

/** Every named row of a list, skipping any that came back without a name. */
const namesOf = (
  rows: (CatalogReference | ServiceReference)[] | null | undefined
): string[] => (rows ?? []).map(nameOf).filter(Boolean);

/** A free list, with the blanks dropped — the wire sends `[""]` often enough. */
const listOf = (values: string[] | null | undefined): string[] =>
  (values ?? []).map((value) => (value ?? "").trim()).filter(Boolean);

/** A figure as the string a screen prints. `null` and `0` are not the same. */
const figureOf = (value: number | null | undefined): string =>
  value === null || value === undefined ? "" : `${value}`;

/** `CM` off the wire, `cm` on the screen. */
const unitOf = (value: string | null | undefined): string =>
  (value ?? "").trim().toLowerCase();

/**
 * `09:00:00` to `09:00`. The seconds are always zero and no dietitian plans a
 * meal around them; `formatTimeRange` takes the shorter form anyway.
 */
const clockOf = (value: string | null | undefined): string =>
  (value ?? "").trim().slice(0, 5);

const toAnswers = (
  form: PreconsultationFormRecord
): PreconsultationFormAnswers => ({
  firstName: (form?.first_name ?? "").trim(),
  lastName: (form?.last_name ?? "").trim(),
  gender: (form?.gender ?? "").trim(),
  age: form?.age ?? null,
  dateOfBirth: (form?.date_of_birth ?? "").trim() || null,
  email: (form?.email ?? "").trim(),
  phone: (form?.phone_number ?? "").trim(),
  address: (form?.full_address ?? "").trim(),
  state: (form?.state ?? "").trim(),
  city: (form?.city ?? "").trim(),
  postalCode: (form?.postal_code ?? "").trim(),
  profession: (form?.profession ?? "").trim(),
  lifestyle: nameOf(form?.life_style),
  goals: namesOf(form?.services ?? form?.goals),
  foodPreference: nameOf(form?.dietary_pattern),
  // The list, and behind it the single field it replaced — a record written
  // before the form asked for several holds its one cuisine there.
  cuisines: namesOf(
    form?.favorite_cuisines ??
      (form?.favorite_cuisine ? [form.favorite_cuisine] : [])
  ),
  conditions: namesOf(form?.medical_conditions),
  height: figureOf(form?.height_value),
  heightUnit: unitOf(form?.height_unit),
  weight: figureOf(form?.weight_value),
  weightUnit: unitOf(form?.weight_unit),
  targetWeight: figureOf(form?.target_weight_value),
  targetWeightUnit: unitOf(form?.target_weight_unit),
  dishesLoved: listOf(form?.favorite_dishes),
  dishesAvoided: listOf(form?.foods_to_avoid),
  foodAllergies: namesOf(form?.food_allergies),
  takesMedications: form?.takes_medications ?? null,
  medications: listOf(form?.medications),
  workStart: clockOf(form?.working_hours_start),
  workEnd: clockOf(form?.working_hours_end),
  noFixedWorkHours: !!form?.has_no_fixed_working_hours,
  sleepStart: clockOf(form?.sleep_start),
  sleepEnd: clockOf(form?.sleep_end),
  noFixedSleep: !!form?.has_no_fixed_sleep_schedule,
  travel: (form?.travel_frequency ?? "").trim(),
  alcohol: (form?.alcohol_frequency ?? "").trim(),
  smoking: (form?.smoking_frequency ?? "").trim(),
});

/**
 * One entry of that list, defended field by field.
 *
 * Everything is optional on the wire and nothing here throws: a client record
 * is drawn whether or not the backend has grown this list yet, and a strip that
 * could not be worked out is one the screen leaves off rather than a screen
 * that fails to load.
 *
 * The fallback status is the caller's, for the reason `toStatus` gives: read
 * off a record, an unrecognised state is safest as `REVIEWED`; read off the
 * answer to a call that just changed the state, it is whatever that call was.
 */
export const toPreconsultationFormSummary = (
  form: PreconsultationFormRecord,
  fallbackStatus: PreconsultationFormStatus = "REVIEWED"
): PreconsultationFormSummary => ({
  id: (form?.public_id ?? "").trim(),
  status: toStatus(form?.status, fallbackStatus),
  statusDisplay: (form?.status_display ?? "").trim(),
  submittedAt: form?.submitted_at?.trim() || null,
  reviewedAt: form?.reviewed_at?.trim() || null,
  expiresAt: form?.expires_at?.trim() || null,
  rejectionReason: (form?.rejection_reason ?? "").trim(),
  createdAt: form?.created_at?.trim() || null,
  shareUrl: (form?.share_url ?? "").trim(),
  answers: toAnswers(form),
});

/**
 * Who the form is for. Exactly one of the two, which is what the endpoint
 * takes — a body carrying both would be asking for two forms.
 */
export type PreconsultationSubject =
  | { clientId: string }
  | { enquiryId: string };

const bodyFor = (subject: PreconsultationSubject) =>
  "clientId" in subject
    ? { client_id: subject.clientId }
    : // `inquiry` on the wire, `enquiry` everywhere above it — the same
      // spelling split the enquiries feature already lives with.
      { inquiry_id: subject.enquiryId };

const toSubjectType = (
  value: string | null | undefined
): PreconsultationSubjectType | null => {
  const held = (value ?? "").trim().toLowerCase();
  return held === "client" || held === "inquiry" ? held : null;
};

const KNOWN_STATUSES: PreconsultationFormStatus[] = [
  "PENDING",
  "SUBMITTED",
  // What `approve` answers with. `REVIEWED` is kept beside it because the
  // backend has used both words for the same moment; either reads as dealt
  // with, which is all any screen here asks of them.
  "APPROVED",
  "REVIEWED",
  "REJECTED",
  "EXPIRED",
];

/**
 * The fallback is the caller's to choose, and the two callers want opposite
 * things from a state this app has not heard of.
 *
 * Raising a form defaults to `PENDING`, because pending is the only thing a
 * form that call just created can be. Reading one back defaults to `REVIEWED`
 * — dealt with — because the strip above a client's tabs acts on what it is
 * told: guessing `PENDING` there would have it claim a live link that nobody
 * can be sure exists, and telling a practitioner their client has an open form
 * when they have not is worse than offering to send another.
 */
const toStatus = (
  value: string | null | undefined,
  fallback: PreconsultationFormStatus = "PENDING"
): PreconsultationFormStatus => {
  const held = (value ?? "").trim().toUpperCase();
  return KNOWN_STATUSES.find((status) => status === held) ?? fallback;
};

/**
 * Raises a form for one client or one enquiry, and hands back the link.
 *
 * A response with no address in it is a failure rather than something to pass
 * on: a dialog offering to copy an empty string is worse than one that says it
 * could not. Everything else on the record is defended and defaulted — the
 * link is the part that has to be right.
 */
export async function createPreconsultationForm(
  getToken: GetToken,
  subject: PreconsultationSubject,
  signal?: AbortSignal
): Promise<PreconsultationFormLink> {
  const token = await getToken();
  if (!token) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error("No active Clerk session - cannot raise a form.");
  }

  const response = await api.post<PreconsultationFormResponse>(
    API_ENDPOINTS.preconsultation.createForm,
    bodyFor(subject),
    { token, signal }
  );

  const url = (response?.share_url ?? "").trim();
  if (!url) {
    throw new ApiError("The server did not return a link.", 500, response);
  }

  return {
    id: (response?.public_id ?? "").trim(),
    url,
    expiresAt: response?.expires_at?.trim() || null,
    status: toStatus(response?.status),
    subject: {
      type: toSubjectType(response?.subject?.type),
      id: (response?.subject?.public_id ?? "").trim(),
      name: (response?.subject?.name ?? "").trim(),
    },
  };
}

/* ── Ruling on one that has come back ────────────────────────────────────── */

/**
 * Accepts a submitted form: its answers are taken onto the record they were
 * asked for, and the form is closed.
 *
 * What comes back is the form itself, `APPROVED`. The merged record is not in
 * it — the client is a separate read, and the screen that accepts re-reads the
 * client rather than trying to assemble one out of this.
 */
export async function approvePreconsultationForm(
  getToken: GetToken,
  formId: string,
  signal?: AbortSignal
): Promise<PreconsultationFormSummary> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot approve a form.");
  }

  return toPreconsultationFormSummary(
    await api.post<PreconsultationFormRecord>(
      API_ENDPOINTS.preconsultation.approveForm(formId),
      // No body. The form is named in the path and the act has no arguments.
      {},
      { token, signal }
    )
  );
}

/**
 * Sends a submitted form back to the client with what to fix.
 *
 * The reason travels; nothing else does. It is the whole of what the client is
 * told, so a caller with nothing to say should not be calling this — the
 * dialog that collects it is what decides whether there is enough to send.
 */
export async function rejectPreconsultationForm(
  getToken: GetToken,
  formId: string,
  reason: string,
  signal?: AbortSignal
): Promise<PreconsultationFormSummary> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot reject a form.");
  }

  return toPreconsultationFormSummary(
    await api.post<PreconsultationFormRecord>(
      API_ENDPOINTS.preconsultation.rejectForm(formId),
      { reason: reason.trim() },
      { token, signal }
    )
  );
}

/* ── Which forms are waiting to be read ──────────────────────────────────── */

/**
 * One submission sitting unread, as the flags on My clients need it.
 *
 * The little of a form that a flag is drawn from: who it belongs to, and how
 * the submission is named. Not the answers — a list screen showing that a form
 * has come back has no business holding forty fields of somebody's health
 * history to say so.
 */
export interface WaitingSubmission {
  /** The client the form was raised for, by `public_id`. */
  clientId: string;
  /** The form's own `public_id`, or `""` where the response named none. */
  formId: string;
  submittedAt: string | null;
  createdAt: string | null;
}

/**
 * The subject of a form, however the response names it.
 *
 * Three spellings tried, because this response's shape is the server's to
 * decide and the flags are not worth a hard dependency on one of them: the
 * `subject` object the create call answers with, a nested client, or a flat id.
 * Anything else reads as no subject, and the entry is dropped.
 */
const subjectIdOf = (form: Record<string, unknown>): string => {
  const subject = form.subject as
    | { type?: string | null; public_id?: string | null }
    | undefined;

  // A form raised for an enquiry belongs to no client, and My clients must not
  // flag a row for it. Only an explicit "inquiry" excludes: a response that
  // names no type at all is taken at its word elsewhere.
  if (subject?.type && toSubjectType(subject.type) === "inquiry") return "";

  const client = form.client as { public_id?: string | null } | undefined;

  return (
    (subject?.public_id ?? client?.public_id ?? form.client_id ?? "") as string
  )
    .toString()
    .trim();
};

/** The rows out of a body that may be a page, a bare array, or neither. */
const rowsOf = (body: unknown): Record<string, unknown>[] => {
  const results = (body as { results?: unknown } | null)?.results;
  const rows = Array.isArray(results) ? results : body;
  return Array.isArray(rows)
    ? rows.filter((row): row is Record<string, unknown> => !!row && typeof row === "object")
    : [];
};

/**
 * Every submission waiting on the practitioner, across all their clients.
 *
 * One request for the whole of it, which is why this exists: the client list
 * does not carry the state of a client's forms, and asking for each client's
 * record in turn to find out would be a hundred requests to put a mark on a
 * list.
 *
 * `SUBMITTED` only — a link still out is waiting on the client and is not news
 * here, and one already accepted or returned has been dealt with.
 *
 * Nothing here throws. The screens that read this draw a flag or draw nothing,
 * and a flag is not worth failing a page over: an endpoint that answers with a
 * shape this cannot read, or does not answer at all, leaves the caller with an
 * empty list and the screens exactly as they were. See `API_ENDPOINTS`.
 */
export async function fetchWaitingSubmissions(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<WaitingSubmission[]> {
  const token = await getToken();
  if (!token) return [];

  const body = await api.get<unknown>(API_ENDPOINTS.preconsultation.forms, {
    token,
    signal,
  });

  return rowsOf(body)
    .filter((form) => toStatus(form.status as string | null) === "SUBMITTED")
    .map((form) => ({
      clientId: subjectIdOf(form),
      formId: ((form.public_id ?? "") as string).toString().trim(),
      submittedAt: ((form.submitted_at ?? "") as string).toString().trim() || null,
      createdAt: ((form.created_at ?? "") as string).toString().trim() || null,
    }))
    .filter((submission) => !!submission.clientId);
}
